"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, Check, Copy } from "lucide-react";
import { useTranslations } from "next-intl";
import { Section } from "@/components/ui/Section";
import { Tooltip } from "@/components/ui/Tooltip";

export const News = () => {
  const t = useTranslations("News");

  // Config
  const isVisible = false;
  const promoCode = "NEWYEARNEWKITTY";

  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(promoCode);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (!isVisible) return null;

  return (
    <Section id="news" className="py-10 md:py-14 bg-[bg-card] relative z-20">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          {/* Main Card Container - Sleek Premium Floating Effect (Fixed Hover) */}
          <div className="bg-(--bg-card)/50 border border-(--border-subtle) rounded-xl shadow-lg transition-all duration-500 overflow-hidden flex flex-col md:flex-row group relative backdrop-blur-sm">
            {/* Ambient Light – orbiting inside the card */}
            <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
              <motion.div
                animate={{
                  // very slow, irregular circular-ish path
                  x: [
                    "-40%",
                    "40%",
                    "40%",
                    "-40%",
                    "-40%",
                    "10%",
                    "-10%",
                    "-40%",
                  ],
                  y: [
                    "-40%",
                    "-40%",
                    "40%",
                    "40%",
                    "-40%",
                    "-10%",
                    "10%",
                    "-40%",
                  ],
                  opacity: [
                    "calc(0.05 + var(--ambient-hover))",
                    "calc(0.12 + var(--ambient-hover))",
                    "calc(0.08 + var(--ambient-hover))",
                    "calc(0.1 + var(--ambient-hover))",
                    "calc(0.06 + var(--ambient-hover))",
                    "calc(0.1 + var(--ambient-hover))",
                    "calc(0.07 + var(--ambient-hover))",
                    "calc(0.05 + var(--ambient-hover))",
                  ],
                }}
                transition={{
                  x: {
                    duration: 60,
                    repeat: Infinity,
                    ease: "easeInOut",
                  },
                  y: {
                    duration: 60,
                    repeat: Infinity,
                    ease: "easeInOut",
                  },
                  opacity: {
                    duration: 60,
                    repeat: Infinity,
                    ease: "easeInOut",
                  },
                }}
                // Fixed: Using CSS transition for interactions to avoid Framer variant/animate conflicts
                // group-hover:brightness-[1.02] adds subtle +2% brightness when card is hovered
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] rounded-full blur-[120px] filter transition-[filter] duration-500 [--ambient-hover:0] group-hover:[--ambient-hover:0.015]"
                style={{
                  background:
                    "radial-gradient(circle at center, var(--brand-primary) 0%, transparent 65%)",
                }}
              />
            </div>

            {/* Left Column: News Label & Icon - More Compact */}
            <div className="w-full md:w-48 md:border-r border-(--border-subtle) p-6 flex flex-col items-center justify-center gap-4 shrink-0 relative z-10">
              <h2 className="text-sm font-bold font-outfit text-(--text-primary) tracking-widest uppercase opacity-90">
                {t("title")}
              </h2>
              <motion.div
                whileHover={{ scale: 1.05, rotate: -6 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="cursor-pointer text-(--text-accent)"
              >
                <Megaphone
                  className="w-8 h-8 md:w-10 md:h-10 fill-current opacity-90"
                  aria-hidden="true"
                />
              </motion.div>
            </div>

            {/* Right Column: Content - Refined Spacing */}
            <div className="flex-1 p-6 md:p-8 flex flex-col items-start gap-4 z-10">
              {/* Meta */}
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono uppercase text-(--text-tertiary) tracking-widest opacity-60">
                  {t("meta")}
                </span>
                <div className="h-px w-8 bg-(--border-subtle)/50" />
              </div>

              {/* Message */}
              <p className="text-(--text-secondary) text-sm md:text-lg font-medium leading-relaxed max-w-lg">
                {t("message")}
              </p>

              {/* Promo Code Capsule (Inline Info Badge) */}
              <div className="relative inline-flex items-center mt-1">
                <Tooltip
                  text={t("click_to_copy")}
                  placement="top"
                  offset={12}
                  className="hidden md:flex"
                >
                  <button
                    onClick={handleCopy}
                    aria-label={t("aria_copy_code", { code: promoCode })}
                    className="relative px-4 py-1.5 rounded-lg border border-dashed border-red-500/20 bg-red-500/5 text-red-600/90 dark:text-red-400/90 font-mono text-sm tracking-wide hover:bg-red-500/10 hover:border-red-500/30 transition-all duration-300 cursor-pointer select-none flex items-center gap-3 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-red-500/30"
                  >
                    <span className="opacity-90 transition-opacity font-medium">
                      {promoCode}
                    </span>

                    {/* Status Icon - Quiet */}
                    <div className="w-3.5 h-3.5 flex items-center justify-center opacity-70 transition-opacity">
                      <AnimatePresence mode="wait">
                        {isCopied ? (
                          <motion.div
                            key="check"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.15 }}
                          >
                            <Check
                              className="w-3 h-3 text-green-500"
                              aria-hidden="true"
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="copy"
                            initial={{ opacity: 1 }}
                            animate={{ opacity: 1 }}
                          >
                            <Copy className="w-3 h-3" aria-hidden="true" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </button>
                </Tooltip>

                {/* Copied Feedback - Quiet Side Note */}
                <AnimatePresence>
                  {isCopied && (
                    <motion.div
                      initial={{ opacity: 0, x: -5 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -5 }}
                      className="absolute left-full top-1/2 -translate-y-1/2 ml-4 text-[10px] font-medium text-green-500 uppercase tracking-widest hidden md:block"
                    >
                      {t("copied")}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer - Minimal */}
              <div className="mt-1 text-[10px] text-(--text-tertiary) font-medium tracking-wide opacity-50">
                {t("footer")}
              </div>
            </div>
          </div>

          {/* Corner Ribbon (Moved Outside to avoid clipping) */}
          <div className="absolute -top-1 -right-1 z-20 overflow-hidden w-40 h-40 pointer-events-none">
            <div className="absolute top-15 -right-10.25 w-55 block transform rotate-45 bg-linear-to-l from-[#ff0000] via-[#d60610] to-[#c80913] text-white text-xs font-bold uppercase text-center leading-5 tracking-widest shadow-md whitespace-nowrap">
              {t("ribbon")}
              {/* Folds */}
              <span className="absolute top-full left-0 border-solid border-4 border-t-[#c80913] border-l-[#c80913] border-r-transparent border-b-transparent content-['']" />
              <span className="absolute top-full right-0 border-solid border-4 border-t-[#c80913] border-r-[#c80913] border-l-transparent border-b-transparent content-['']" />
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};
